/** Retained world calibration: existing obstacle footprints were authored against this sole offset. */
export const BODY_GROUND_OFFSET = 21.6
export const BODY_RENDER_LIFT = -6.4

/** Foot reference in the body's native frame, not the canonical 64px metadata space. */
export const bodyFootPosition = (width: number, height: number, feetY = 60) => ({
    x: width / 2,
    y: height / 2 + feetY - 32,
})

/** Centre every body's soles on the same 64px ground reference used to author obstacles. */
export const bodyFramePlacement = (width: number, height: number, anchor: number | { x: number; y: number } = 60) => {
    const feet = typeof anchor === "number" ? bodyFootPosition(width, height, anchor) : anchor
    return { x: 32 - feet.x, y: 60 - feet.y }
}

/** Align the bottom centre of visible pixels to the fixed ground reference. */
export const alignBodyPixels = (pixels: ArrayLike<number>, width: number, height: number,
    feet: { x: number; y: number }, bakeOffset: { x: number; y: number }) => {
    let left = width, right = -1, bottom = -1
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (pixels[(y * width + x) * 4 + 3] > 0) {
            left = Math.min(left, x); right = Math.max(right, x); bottom = y
        }
    }
    return bottom < 0 ? undefined : {
        x: Math.round((feet.x - (left + right + 1) / 2 - bakeOffset.x) * 10) / 10,
        y: Math.round((feet.y - (bottom + 1) - bakeOffset.y) * 10) / 10,
    }
}

/** Legacy metadata fallback for catalogs that have not yet authored a ground anchor. */
export const resolveBodyGroundAnchor = (width: number, height: number,
    entry?: { groundAnchor?: { x: number; y: number }; shadow?: { feetY: number } }) =>
    entry?.groundAnchor ?? bodyFootPosition(width, height, entry?.shadow?.feetY)

/** Shared by catalog compilation, Sprite Lab saves, and registry tooling. */
export const bodyPlacementErrors = (entry: any, slot: string): string[] => {
    const errors: string[] = []
    for (const field of ["placement", "groundAnchor"]) {
        const point = entry[field]
        if (point === undefined) continue
        if (slot !== "body" || !point || Array.isArray(point) ||
            Object.keys(point).some(k => k !== "x" && k !== "y") ||
            !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
            errors.push(`${field} x and y must be finite numbers on a body`)
        }
    }
    if (entry.depthBias !== undefined && (slot !== "body" || !Number.isFinite(entry.depthBias)))
        errors.push("depthBias must be a finite number on a body")
    return errors
}

/** Stable south-facing body reference, independent of the selected action or held equipment. */
export const neutralBodyFrameName = (names: string[]): string | undefined => {
    const sorted = [...names].sort((a, b) => a.localeCompare(b, "en", { numeric: true }))
    for (const action of ["walk-front", "walk-down", "idle-front", "idle-down"])
        if (sorted.some(name => name === action || name.startsWith(`${action}-`)))
            return sorted.find(name => name === action || name.startsWith(`${action}-`))
    return sorted.find(name => /^(walk|idle)-/.test(name)) ?? sorted[0]
}
